import type { ParsedUrlQuery } from 'querystring'

import type {
  LoadTaskRedirectResponse,
  TaskRedirect,
} from 'shared/gen/client/web/proto/project_migration'
import { Treatments } from 'shared/services/feature-flags/server'

//============---------==========-----------============//
// Types

/** 1.0 internal project pages under /app/projects/{legacyId}/... */
export type LegacyProjectPage =
  | 'index'
  | 'overview'
  | 'plan'
  | 'plan-list'
  | 'plan-gantt'
  | 'my-tasks'
  | 'messages'
  | 'team'
  | 'files'
  | 'custom-fields'
  | 'activity-log'
  | 'notes'
  | 'action-items'
  | 'billable-rates'

/** 1.0 Compass project pages under /customer/{page}/{legacyId} */
export type CustomerPortalPage =
  | 'today'
  | 'messaging'
  | 'notes'
  | 'attachments'
  | 'resources'

export type RedirectSource =
  | { app: 'legacy'; page: LegacyProjectPage }
  | { app: 'customer'; page: CustomerPortalPage }

/** The {action} segment of /task-actions/{action}/{taskId}[/{userId}] */
export type TaskActionName =
  | 'completed'
  | 'done'
  | 'stuck'
  | 'working-on-it'
  | 'scheduled'
  | 'group-taskcompleted'
  | 'sign-off-approved'
  | 'sign-off-rejected'

export const TASK_ACTION_NAMES: readonly TaskActionName[] = [
  'completed',
  'done',
  'stuck',
  'working-on-it',
  'scheduled',
  'group-taskcompleted',
  'sign-off-approved',
  'sign-off-rejected',
]

export type RedirectTreatments =
  | Partial<Record<Treatments, boolean>>
  | undefined

//============---------==========-----------============//
// Gate

export const ADMIN_ROLES: readonly string[] = ['ADMIN', 'SUPER_ADMIN']

export function isTaskActionName(value: unknown): value is TaskActionName {
  return (
    typeof value === 'string' &&
    (TASK_ACTION_NAMES as readonly string[]).includes(value)
  )
}

/**
 * Workspace admins and super admins keep 1.0 access for reference, so they are
 * never redirected. The backend applies the same bypass; this only saves the
 * call.
 */
export function isAdminRole(role: string | null | undefined): boolean {
  return typeof role === 'string' && ADMIN_ROLES.includes(role)
}

/**
 * Whether the `migrate_projects_to_v2` flag is on. Every live-on-2.0 lookup
 * except the task-action redirect checks this before anything else; when it
 * is off no RPC is made.
 *
 * The flag rolls out per workspace. Anonymous callers (task-action email
 * links) have no workspace, so their treatments were evaluated with Split's
 * 'BASIC' key, which is why redirectTaskActionIfLiveOnV2 skips this check and
 * lets the backend decide.
 */
export function isMigrationFlagOn(treatments: RedirectTreatments): boolean {
  return treatments?.[Treatments.migrateProjectsToV2] ?? false
}

/**
 * Whether a redirect-only lookup should happen at all: the flag is on and the
 * caller is not an admin, since admins are never redirected. Pages that also
 * render for admins (the legacy project pages' banner) gate on
 * isMigrationFlagOn alone and let the backend's should_redirect decide.
 */
export function shouldCheckRedirect(args: {
  treatments: RedirectTreatments
  role: string | null | undefined
}): boolean {
  return isMigrationFlagOn(args.treatments) && !isAdminRole(args.role)
}

//============---------==========-----------============//
// Query strings

/**
 * Next merges route params (projectId, slug) into `context.query`; those must
 * not leak into the destination. Values are URL-encoded, array values repeat
 * the key.
 */
export function toSearchParams(
  query: ParsedUrlQuery,
  omit: readonly string[] = [],
): URLSearchParams {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (omit.includes(key) || value === undefined) continue
    for (const item of Array.isArray(value) ? value : [value]) {
      params.append(key, item)
    }
  }
  return params
}

export function toQueryString(
  query: ParsedUrlQuery,
  omit: readonly string[] = [],
): string {
  return withQuery('', toSearchParams(query, omit))
}

/**
 * The 1.0 project channel is keyed `messageKey=legacyProjectId&messageId={legacyId}`;
 * 2.0 keys it `messageKey=projectId&messageId={v2}`. Only that key is rewritten:
 * other request keys (messageId, channelId, ...) already carry K2 ids and pass
 * through untouched. An absent key means "the project channel", so it is
 * filled in with the 2.0 key.
 */
export function rewriteMessagingQuery(
  query: ParsedUrlQuery,
  v2ProjectId: string,
): URLSearchParams {
  const params = toSearchParams(query, ROUTE_PARAMS)
  const messageKey = params.get('messageKey')
  if (messageKey === null || messageKey === 'legacyProjectId') {
    params.set('messageKey', 'projectId')
    params.set('messageId', v2ProjectId)
  }
  return params
}

//============---------==========-----------============//
// Destinations

/**
 * The 2.0 URL for a 1.0 project page, per the redirect surface map. Pages with
 * no 2.0 equivalent fall back to the project overview (internal) or today
 * (portal); main's own permission check moves a customer from the overview to
 * the portal.
 */
export function buildV2Destination(args: {
  source: RedirectSource
  v2ProjectId: string
  query: ParsedUrlQuery
  treatments: RedirectTreatments
}): string {
  const { source, v2ProjectId: v2, query, treatments } = args

  if (source.app === 'customer') {
    const portal = `/portal/${v2}`
    switch (source.page) {
      case 'messaging':
      case 'notes':
        return withQuery(`${portal}/messages`, rewriteMessagingQuery(query, v2))
      case 'attachments':
        return `${portal}/attachments`
      case 'resources':
        return treatments?.[Treatments.compassAndPortalResources]
          ? `${portal}/resources`
          : `${portal}/today`
      case 'today':
      default:
        return `${portal}/today`
    }
  }

  const project = `/project/${v2}`
  switch (source.page) {
    case 'plan':
    case 'plan-list':
    case 'plan-gantt':
      return withQuery(
        `${project}/plan`,
        withLeadingParam(
          'view',
          PLAN_VIEW_BY_PAGE[source.page],
          toSearchParams(query, [...ROUTE_PARAMS, 'view']),
        ),
      )
    case 'messages':
    case 'notes':
      return withQuery(`${project}/messages`, rewriteMessagingQuery(query, v2))
    case 'team':
      return withQuery(`${project}/team`, toSearchParams(query, ROUTE_PARAMS))
    case 'files':
      return `${project}/attachments`
    case 'custom-fields':
      return `${project}/custom-fields`
    case 'activity-log':
      return treatments?.[Treatments.projectHistory]
        ? `${project}/history`
        : `${project}/overview`
    // my-tasks has no 2.0 page. It lands on the overview like the other
    // fallbacks; main's project layout then sends anyone without full project
    // access to the portal, so customers still end up on /portal/{v2}/today.
    case 'my-tasks':
    case 'index':
    case 'overview':
    case 'action-items':
    case 'billable-rates':
    default:
      return `${project}/overview`
  }
}

/**
 * The 2.0 URL for a 1.0 task-action email link. Status actions land on the
 * public /unit-actions page when the backend resolved the acting project
 * workspace member; everything else (and any unresolved member) lands on the
 * project plan. Returns undefined when the caller should stay on 1.0.
 */
export function buildV2TaskActionDestination(args: {
  action: TaskActionName
  response: Pick<
    LoadTaskRedirectResponse,
    'shouldRedirect' | 'projectId' | 'unitId' | 'projectWorkspaceMemberId'
  >
}): string | undefined {
  const { action, response } = args
  const projectId = response.projectId?.uuid
  if (!response.shouldRedirect || !projectId) return undefined

  const unitAction = UNIT_ACTION_BY_TASK_ACTION[action]
  const unitId = response.unitId?.uuid
  const memberId = response.projectWorkspaceMemberId?.uuid
  if (unitAction && unitId && memberId) {
    return `/unit-actions/${unitAction}/${unitId}/${memberId}`
  }
  return `/project/${projectId}/plan`
}

/**
 * The 2.0 URL for a 1.0 task row that is live on 2.0: the project plan with
 * the task's drawer open (`task-id` drives the drawer on every 2.0 page)
 * when the unit is known, the plan alone when only the project is. Returns
 * undefined when the caller should stay on 1.0 or no project is known. The
 * destination is a different Next app on the same origin, so callers render
 * it as a full navigation, never a client-side route.
 */
export function buildV2TaskDestination(
  redirect: Pick<TaskRedirect, 'shouldRedirect' | 'projectId' | 'unitId'>,
): string | undefined {
  if (!redirect.shouldRedirect) return undefined
  const projectId = redirect.projectId?.uuid
  if (!projectId) return undefined
  const plan = `/project/${projectId}/plan`
  const unitId = redirect.unitId?.uuid
  return unitId ? `${plan}?task-id=${unitId}` : plan
}

/**
 * The 2.0 time records page for a 1.0 time entry target whose project is
 * live on 2.0: filtered to the project when its 2.0 id is known (that page
 * reads `projectId` from the URL), the page alone otherwise. main is a
 * different Next app on the same origin, so callers render it as a full
 * navigation.
 */
export function buildV2TimeTrackingDestination(v2ProjectId?: string): string {
  const page = '/time-tracking/time-records'
  return v2ProjectId ? `${page}?projectId=${v2ProjectId}` : page
}

//============---------==========-----------============//
// PRIVATE

const ROUTE_PARAMS: readonly string[] = ['projectId', 'slug']

const PLAN_VIEW_BY_PAGE: Record<
  Extract<LegacyProjectPage, 'plan' | 'plan-list' | 'plan-gantt'>,
  'board' | 'list' | 'gantt'
> = {
  plan: 'board',
  'plan-list': 'list',
  'plan-gantt': 'gantt',
}

// 1.0 emails emit "completed", never "done", but the 1.0 /task-actions/done
// page exists so it is mapped defensively.
const UNIT_ACTION_BY_TASK_ACTION: Partial<
  Record<TaskActionName, 'done' | 'stuck' | 'working-on-it'>
> = {
  completed: 'done',
  done: 'done',
  stuck: 'stuck',
  'working-on-it': 'working-on-it',
}

function withQuery(path: string, params: URLSearchParams): string {
  const search = params.toString()
  return search ? `${path}?${search}` : path
}

function withLeadingParam(
  key: string,
  value: string,
  rest: URLSearchParams,
): URLSearchParams {
  const params = new URLSearchParams([[key, value]])
  rest.forEach((v, k) => params.append(k, v))
  return params
}
